import { formatCurrency } from "@/lib/utils";
import type { Position } from "@/solana/programs/types";

// Site's green and red colors (matching portfolio cards)
const SITE_GREEN = "#2ebd85";
const SITE_GREEN_LIGHT = "#5fd4a4";
const SITE_RED = "#f6465d";
const SITE_RED_LIGHT = "#ff8a9a";

type SharePositionCardProps = {
  position: Position;
  pnl: number;
  exitPrice: number;
};

export const SharePositionCard = ({ position, pnl, exitPrice }: SharePositionCardProps) => {
  const isProfitable = pnl >= 0;
  const glowColor = isProfitable ? SITE_GREEN : SITE_RED;
  const glowColorLight = isProfitable ? SITE_GREEN_LIGHT : SITE_RED_LIGHT;
  
  // Calculate ROI percentage
  const entryValue = position.size * position.entryPrice;
  const margin = position.margin || entryValue / position.leverage;
  const roiPercent = margin > 0 ? (pnl / margin) * 100 : 0;
  
  // Format date
  const closeDate = new Date().toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <div
      id="share-card"
      style={{
        width: "600px",
        padding: "0",
        background: "#0a0a0f",
        borderRadius: "20px",
        color: "#ffffff",
        fontFamily: "'Space Grotesk', 'Inter', sans-serif",
        boxSizing: "border-box",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated gradient border effect (static for image) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "20px",
          padding: "2px",
          background: `linear-gradient(135deg, ${glowColor}40 0%, transparent 40%, transparent 60%, ${glowColorLight}60 100%)`,
          WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
        }}
      />

      {/* Outer glow effect */}
      <div
        style={{
          position: "absolute",
          inset: "-20px",
          borderRadius: "40px",
          background: `radial-gradient(ellipse at center, ${glowColor}15 0%, transparent 70%)`,
          zIndex: 0,
        }}
      />

      {/* Background pattern/texture */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          opacity: 0.03,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          zIndex: 0,
        }}
      />

      {/* Content */}
      <div style={{ position: "relative", zIndex: 1, padding: "32px" }}>
        {/* Header with branding */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "24px",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "11px",
                fontWeight: "600",
                letterSpacing: "2px",
                color: "#666",
                textTransform: "uppercase",
                marginBottom: "4px",
              }}
            >
              Trade Closed
            </div>
            <div style={{ fontSize: "13px", color: "#808080" }}>{closeDate}</div>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div
              style={{
                fontSize: "20px",
                fontWeight: "700",
                letterSpacing: "-0.5px",
                background: "linear-gradient(135deg, #fff 0%, #888 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              NORDEX
            </div>
          </div>
        </div>

        {/* Market & Side Badge */}
        <div style={{ marginBottom: "20px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              marginBottom: "8px",
            }}
          >
            <div
              style={{
                fontSize: "36px",
                fontWeight: "800",
                letterSpacing: "-1px",
                color: "#ffffff",
              }}
            >
              {position.market}
            </div>
            <div
              style={{
                display: "flex",
                gap: "8px",
              }}
            >
              <div
                style={{
                  padding: "6px 14px",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: "700",
                  letterSpacing: "0.5px",
                  backgroundColor: position.side === "long" ? `${SITE_GREEN}20` : `${SITE_RED}20`,
                  color: position.side === "long" ? SITE_GREEN : SITE_RED,
                  border: `1px solid ${position.side === "long" ? `${SITE_GREEN}40` : `${SITE_RED}40`}`,
                  textTransform: "uppercase",
                }}
              >
                {position.side}
              </div>
              <div
                style={{
                  padding: "6px 14px",
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: "700",
                  backgroundColor: "rgba(255, 255, 255, 0.05)",
                  color: "#fff",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                }}
              >
                {position.leverage}×
              </div>
            </div>
          </div>
        </div>

        {/* Main P&L Display - Hero Section */}
        <div
          style={{
            padding: "28px",
            marginBottom: "20px",
            borderRadius: "16px",
            background: isProfitable
              ? "linear-gradient(135deg, rgba(46, 189, 133, 0.15) 0%, rgba(46, 189, 133, 0.05) 100%)"
              : "linear-gradient(135deg, rgba(246, 70, 93, 0.15) 0%, rgba(246, 70, 93, 0.05) 100%)",
            border: `1px solid ${isProfitable ? "rgba(46, 189, 133, 0.3)" : "rgba(246, 70, 93, 0.3)"}`,
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Inner glow */}
          <div
            style={{
              position: "absolute",
              top: "-50%",
              left: "50%",
              transform: "translateX(-50%)",
              width: "200px",
              height: "200px",
              borderRadius: "50%",
              background: `radial-gradient(circle, ${glowColor}20 0%, transparent 70%)`,
              zIndex: 0,
            }}
          />

          <div style={{ position: "relative", zIndex: 1 }}>
            <div
              style={{
                fontSize: "12px",
                fontWeight: "600",
                letterSpacing: "2px",
                color: "#888",
                textTransform: "uppercase",
                marginBottom: "12px",
              }}
            >
              Realized P&L
            </div>
            <div
              style={{
                fontSize: "56px",
                fontWeight: "800",
                letterSpacing: "-2px",
                color: isProfitable ? SITE_GREEN : SITE_RED,
                lineHeight: 1,
                marginBottom: "8px",
                textShadow: `0 0 40px ${glowColor}60`,
              }}
            >
              {isProfitable ? "+" : "-"}${formatCurrency(Math.abs(pnl), 2)}
            </div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 16px",
                borderRadius: "20px",
                backgroundColor: isProfitable ? "rgba(46, 189, 133, 0.2)" : "rgba(246, 70, 93, 0.2)",
                fontSize: "18px",
                fontWeight: "700",
                color: isProfitable ? SITE_GREEN_LIGHT : SITE_RED_LIGHT,
              }}
            >
              {isProfitable ? "↑" : "↓"} {roiPercent >= 0 ? "+" : ""}{roiPercent.toFixed(1)}% ROI
            </div>
          </div>
        </div>

        {/* Trade Details Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "12px",
            marginBottom: "24px",
          }}
        >
          <div
            style={{
              padding: "16px",
              borderRadius: "12px",
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              border: "1px solid rgba(255, 255, 255, 0.06)",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "11px", color: "#666", marginBottom: "6px", letterSpacing: "0.5px" }}>
              Entry Price
            </div>
            <div style={{ fontSize: "18px", fontWeight: "700", color: "#fff" }}>
              ${formatCurrency(position.entryPrice, 2)}
            </div>
          </div>
          <div
            style={{
              padding: "16px",
              borderRadius: "12px",
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              border: "1px solid rgba(255, 255, 255, 0.06)",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "11px", color: "#666", marginBottom: "6px", letterSpacing: "0.5px" }}>
              Exit Price
            </div>
            <div style={{ fontSize: "18px", fontWeight: "700", color: "#fff" }}>
              ${formatCurrency(exitPrice, 2)}
            </div>
          </div>
          <div
            style={{
              padding: "16px",
              borderRadius: "12px",
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              border: "1px solid rgba(255, 255, 255, 0.06)",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "11px", color: "#666", marginBottom: "6px", letterSpacing: "0.5px" }}>
              Size
            </div>
            <div style={{ fontSize: "18px", fontWeight: "700", color: "#fff" }}>
              {formatCurrency(position.size, 4)}
            </div>
          </div>
        </div>

        {/* Footer with branding */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: "16px",
            borderTop: "1px solid rgba(255, 255, 255, 0.06)",
          }}
        >
          <div
            style={{
              fontSize: "13px",
              color: "#555",
              fontWeight: "500",
            }}
          >
            Trade on <span style={{ color: "#888", fontWeight: "600" }}>app.nordex.trade</span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              style={{ color: "#444" }}
            >
              <path
                d="M12 2L2 7L12 12L22 7L12 2Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 17L12 22L22 17"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 12L12 17L22 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span style={{ fontSize: "11px", color: "#444", fontWeight: "500", letterSpacing: "1px" }}>
              PERPETUALS
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
